import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Globe, Menu, X, LogIn, LogOut, ShieldCheck, User } from "lucide-react";
import { useI18n } from "../../contexts/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { api, fileUrl } from "../../lib/api";

const NAV = [
  ["/", "home"], ["/about", "about"], ["/districts", "districts"],
  ["/courses", "courses"], ["/books", "books"],
  ["/volunteers", "volunteers"], ["/programmes", "programmes"],
  ["/gallery", "gallery"], ["/team", "team"], ["/contact", "contact"], ["/donate", "donate"],
];

export default function Header() {
  const { t, lang, setLang, langs } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const languageRef = useRef(null);
  useEffect(() => {
    const close = e => { if (!languageRef.current?.contains(e.target)) setLanguageOpen(false); };
    const escape = e => { if (e.key === "Escape") setLanguageOpen(false); };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", escape); };
  }, []);
  const [q, setQ] = useState("");
  const [settings, setSettings] = useState(null);

  useEffect(() => { api.get("/settings").then(r => setSettings(r.data)).catch(() => {}); }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) { nav(`/search?q=${encodeURIComponent(q)}`); setOpen(false); }
  };

  const logo = settings?.logo_url ? fileUrl(settings.logo_url) : null;

  return (
    <header className="sticky top-0 z-50 border-b border-[var(--js-border)]" style={{ background: "var(--js-bg)" }} data-testid="site-header">
      <div className="container-narrow flex items-center justify-between gap-3 px-4 py-3">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-11 h-11 rounded-full border border-[var(--js-border)] bg-white flex items-center justify-center overflow-hidden">
            {logo ? <img src={logo} alt="logo" className="w-full h-full object-cover" /> : <span className="font-heading text-[var(--js-primary)] text-lg">ॐ</span>}
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-heading text-lg text-[var(--js-primary)]">{t("org")}</div>
            <div className="text-[11px] text-[var(--js-muted)]">{t("tagline")}</div>
          </div>
        </Link>

        <form onSubmit={submit} className="hidden md:flex items-center flex-1 max-w-md mx-6" data-testid="header-search-form">
          <div className="relative w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--js-muted)]" />
            <input data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="w-full pl-9 pr-3 py-2 rounded-full border border-[var(--js-border)] bg-white text-sm focus:outline-none focus:border-[var(--js-primary)]" />
          </div>
        </form>

        <div className="flex items-center gap-2">
          <div className="relative" ref={languageRef}>
            <button type="button" onClick={() => setLanguageOpen(value => !value)} aria-haspopup="menu" aria-expanded={languageOpen} aria-controls="language-menu" className="flex items-center gap-1 px-3 py-2 rounded-full border border-[var(--js-border)] bg-white text-sm hover:border-[var(--js-primary)] transition-colors" data-testid="lang-switcher">
              <Globe size={14} /> {langs.find(l => l.code === lang)?.native}
            </button>
            {languageOpen && <div id="language-menu" role="menu" className="absolute right-0 mt-1 w-40 bg-white border border-[var(--js-border)] rounded-md shadow-lg z-50" data-testid="lang-menu">
              {langs.map(l => (
                <button type="button" role="menuitemradio" aria-checked={lang === l.code} key={l.code} onClick={() => { setLang(l.code); setLanguageOpen(false); }} data-testid={`lang-option-${l.code}`} className={`block w-full text-left px-3 py-2 text-sm hover:bg-[var(--js-bg)] ${lang === l.code ? "text-[var(--js-primary)] font-semibold" : ""}`}>{l.native}</button>
              ))}
            </div>}
          </div>
          {user ? (
            <UserMenu user={user} />
          ) : (
            <Link to="/login" data-testid="login-btn" className="js-btn-outline !py-1.5 !px-3 !text-xs"><LogIn size={14} /> Sign in</Link>
          )}
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </div>
      <nav className="hidden lg:block border-t border-[var(--js-border)] bg-white/60" data-testid="desktop-nav">
        <div className="container-narrow flex items-center gap-1 px-4 py-2 overflow-x-auto">
          {NAV.map(([path, key]) => (
            <NavLink key={path} to={path} end={path === "/"} data-testid={`nav-${key}`} className={({ isActive }) => `px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${isActive ? "bg-[var(--js-primary)] text-white" : "text-[var(--js-text)] hover:bg-[var(--js-border)]/60"}`}>{t(`nav.${key}`)}</NavLink>
          ))}
        </div>
      </nav>
      {open && (
        <div className="lg:hidden border-t border-[var(--js-border)] bg-white p-4 space-y-1" data-testid="mobile-nav">
          <form onSubmit={submit} className="mb-3">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="w-full px-3 py-2 rounded-full border border-[var(--js-border)] text-sm" data-testid="mobile-search-input" />
          </form>
          {NAV.map(([path, key]) => (
            <NavLink key={path} to={path} end={path === "/"} onClick={() => setOpen(false)} data-testid={`mobile-nav-${key}`} className={({ isActive }) => `block px-3 py-2 rounded-md text-sm ${isActive ? "bg-[var(--js-primary)] text-white" : "hover:bg-[var(--js-bg)]"}`}>{t(`nav.${key}`)}</NavLink>
          ))}
        </div>
      )}
    </header>
  );
}

function UserMenu({ user }) {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const initials = (user.name || user.email || "U").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} data-testid="user-menu-btn" className="flex items-center gap-2 px-2 py-1 rounded-full border border-[var(--js-border)] bg-white hover:border-[var(--js-primary)] transition-colors">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-[var(--js-primary)] text-white text-xs font-semibold flex items-center justify-center">{initials}</div>
        )}
        <span className="hidden sm:block text-xs font-semibold max-w-[120px] truncate">{user.name || user.email}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-[var(--js-border)] rounded-md shadow-lg z-50 overflow-hidden" data-testid="user-menu-dropdown">
          <div className="px-3 py-3 border-b border-[var(--js-border)]">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-xs text-[var(--js-muted)] truncate">{user.email}</div>
            {user.is_admin && <div className="mt-2"><span className="js-chip js-chip-paid">Admin</span></div>}
          </div>
          {user.is_admin && (
            <Link to="/admin" onClick={() => setOpen(false)} data-testid="admin-nav-btn" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--js-bg)]">
              <ShieldCheck size={14} /> Admin Dashboard
            </Link>
          )}
          <button onClick={() => { setOpen(false); logout(); }} data-testid="logout-btn" className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--js-bg)] text-[var(--js-primary)]">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
